import { type CSSProperties, type ReactNode, useEffect, useRef } from 'react';
import { useMergedRef, useResizeObserver } from '@mantine/hooks';
import clsx from 'clsx';

import { Button } from '../../buttons/Button';
import { GoogleFontIcon } from '../../icons/GoogleFontIcon';
import { TextWithTooltip } from '../../tooltip/TextWithTooltip';

import { useScrollingState } from './useScrollingState';

import styles from './horizontal-navigation.module.scss';

const OVERFLOW_SCROLL_TOLERANCE = 30;

export type NavigationItemData = {
  label: string;
  completeSlug: string;
  items?: NavigationItemData[];
};

export type HorizontalNavigationProps = {
  isOpen?: boolean;
  activeColumnIndex?: number;
  navigation: NavigationItemData[];
  style?: CSSProperties;
  Link?: (item: NavigationItemData) => React.JSX.Element;
  trailingActions?: ReactNode;
};

export function HorizontalNavigation({
  navigation,
  isOpen,
  activeColumnIndex,
  style,
  Link,
  trailingActions,
}: HorizontalNavigationProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [resizeRef] = useResizeObserver<HTMLDivElement>(); // re-render on resize
  const ref = useMergedRef(scrollContainerRef, resizeRef);
  useScrollingState(scrollContainerRef); // re-render after scrolling stops
  const navigationItemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const $el = scrollContainerRef.current;

  let hasOverflow = false;
  let canScrollLeft = false;
  let canScrollRight = false;
  if ($el) {
    hasOverflow = $el.scrollWidth > $el?.clientWidth;
    canScrollLeft = $el.scrollLeft > 0;
    canScrollRight =
      $el.scrollLeft + OVERFLOW_SCROLL_TOLERANCE <
      $el.scrollWidth - $el.clientWidth;
  }

  function scrollRight() {
    const el = scrollContainerRef.current;
    const step = el ? getHorizontalScrollStep(el) : null;
    if (el && step) {
      const { itemWidth, gap } = step;
      const lastItemIndexVisible = Math.floor(
        (el.scrollLeft + el.clientWidth) / (itemWidth + gap) - 1
      );
      el.scrollTo({
        left: (lastItemIndexVisible + 1) * (itemWidth + gap),
        behavior: 'smooth',
      });
    }
  }

  function scrollLeft() {
    const el = scrollContainerRef.current;
    const step = el ? getHorizontalScrollStep(el) : null;
    if (el && step) {
      const { itemWidth, gap } = step;
      const firstItemIndexVisible = Math.round(
        el.scrollLeft / (itemWidth + gap)
      );
      const itemsInView = Math.floor(el.clientWidth / (itemWidth + gap));
      const firstItem = Math.max(0, firstItemIndexVisible - itemsInView);

      el.scrollTo({
        left: firstItem * (itemWidth + gap),
        behavior: 'smooth',
      });
    }
  }

  useEffect(() => {
    if (activeColumnIndex !== undefined) {
      navigationItemRefs.current[activeColumnIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [activeColumnIndex, navigation]); // Always scroll to the active index, even if the navigation data changes

  return (
    <div
      className={clsx(styles['root'])}
      data-testid="horizontal-navigation"
      data-open={isOpen === true}
      style={style}
    >
      <div className={styles['main']}>
        <div
          ref={ref}
          className={clsx(
            styles['row'],
            isOpen ? styles['row--is-opened'] : styles['row--is-closed']
          )}
        >
          {navigation.map((item, index) => (
            <div
              ref={(ref) => {
                navigationItemRefs.current[index] = ref;
              }}
              key={index}
              className={clsx(
                styles['column'],
                activeColumnIndex === index
                  ? styles['column--is-active']
                  : styles['column--is-inactive'],
                isOpen ? styles['column--is-open'] : styles['column--is-closed']
              )}
            >
              <div className={styles['column__header']} data-type="nav-header">
                <HorizontalNavigationLink item={item} Link={Link} />
              </div>

              {isOpen && (
                <div
                  className={styles['column__items']}
                  style={{ visibility: isOpen ? 'visible' : 'hidden' }}
                >
                  {'items' in item &&
                    item.items?.map((item) => (
                      <HorizontalNavigationItem
                        key={item.completeSlug}
                        item={item}
                        Link={Link}
                      />
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {hasOverflow && canScrollLeft && (
          <Button
            className={clsx(styles['overflow'], styles['overflow--left'])}
            onClick={scrollLeft}
            variant="secondary"
            data-testid="horizontal-navigation-scrollleft"
          >
            <GoogleFontIcon name="navigate_before" />
          </Button>
        )}
        {hasOverflow && canScrollRight && trailingActions == null && (
          <Button
            className={clsx(styles['overflow'], styles['overflow--right'])}
            onClick={scrollRight}
            variant="secondary"
            data-testid="horizontal-navigation-scrollright"
          >
            <GoogleFontIcon name="navigate_next" />
          </Button>
        )}
        <div className={styles['dropout-divider']}></div>
        <div className={styles['dropout-bg']}></div>
      </div>
      {trailingActions != null || (hasOverflow && canScrollRight) ? (
        <div className={styles['trailingActions']}>
          {hasOverflow && canScrollRight && trailingActions != null && (
            <Button
              onClick={scrollRight}
              variant="secondary"
              data-testid="horizontal-navigation-scrollright"
            >
              <GoogleFontIcon name="navigate_next" />
            </Button>
          )}
          {trailingActions}
        </div>
      ) : null}
    </div>
  );
}

export type HorizontalNavigationItemProps = {
  item: NavigationItemData;
  Link?: (item: NavigationItemData) => React.JSX.Element;
};
export function HorizontalNavigationItem({
  item,
  Link,
}: HorizontalNavigationItemProps) {
  if (item.items == null) {
    return <HorizontalNavigationLink item={item} Link={Link} />;
  }
  return (
    <div className={styles['section']}>
      <div className={styles['section__header']}>{item.label}</div>
      <div className={styles['section__items']}>
        {item.items.map((i, index) => (
          <HorizontalNavigationItem key={i.completeSlug} item={i} Link={Link} />
        ))}
      </div>
    </div>
  );
}

export type HorizontalNavigationLinkProps = {
  item: NavigationItemData;
  Link?: (item: NavigationItemData) => React.JSX.Element;
};
export function HorizontalNavigationLink({
  item,
  Link = (item) => <LinkContent item={item} />,
}: HorizontalNavigationLinkProps) {
  return <div className={styles['nav-link']}>{Link(item)}</div>;
}

export function LinkContent({ item }: { item: NavigationItemData }) {
  return (
    <TextWithTooltip textProps={{ lineClamp: 2 }}>{item.label}</TextWithTooltip>
  );
}

function getHorizontalScrollStep(el: HTMLDivElement) {
  const first = el.children[0];
  const second = el.children[1];
  if (!(first instanceof HTMLElement) || !(second instanceof HTMLElement)) {
    return null;
  }

  const itemWidth = first.clientWidth;
  return { itemWidth, gap: second.offsetLeft - itemWidth };
}
